import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TimesIcon } from '@c2fo/liquidity/icons';
import useMarkTermsAsAcknowledged from '@/enterprise/data/useMarkTermsAsAcknowledged';
import useMarkTermsAsSeen from '@/enterprise/data/useMarkTermsAsSeen';
import { useShowPrivacyPolicyBanner } from '@/enterprise/data/useTerms';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { useAgreementsUrl } from '@/utils/useAgreementsUrl';
import useDataDog from '../../../reporting/DataDog';

const PrivacyPolicyBannerCore = () => {
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const { mutate: markTermsAsSeen } = useMarkTermsAsSeen();
  const { mutate: markTermsAsAcknowledged } = useMarkTermsAsAcknowledged();
  const { privacyLink } = useAgreementsUrl();
  const { latestPrivacyPolicy } = useShowPrivacyPolicyBanner();
  const { t } = useTranslation();
  const { addAction } = useDataDog();

  useEffect(() => {
    if (latestPrivacyPolicy && !latestPrivacyPolicy.acknowledgedAt) {
      addAction('enterprise::privacy-policy-banner::shown', {
        message: `Privacy policy banner shown`,
        termsDocumentUuid: latestPrivacyPolicy.uuid,
      });

      const delayToMarkAsSeen = 10000;
      const timer = setTimeout(() => {
        markTermsAsSeen({ termsDocumentUuid: latestPrivacyPolicy.uuid });
        setTimer(null);
      }, delayToMarkAsSeen);

      setTimer(timer);

      return () => clearTimeout(timer);
    }
  }, [latestPrivacyPolicy, latestPrivacyPolicy?.acknowledgedAt, markTermsAsSeen, addAction]);

  const handlePrivacyLinkClick = () => {
    addAction('enterprise::privacy-policy-banner::clicked', {
      message: `Privacy policy banner clicked`,
      termsDocumentUuid: latestPrivacyPolicy?.uuid,
    });
    if (latestPrivacyPolicy) {
      markTermsAsAcknowledged(latestPrivacyPolicy.uuid);
    }
  };

  /**
   * If user clicks the dismiss button before the timer runs out, the banner margin does not get adjusted.
   * So we need to clear the timer and then call markTermsAsAcknowledged.
   * The API call is made only once, either after the timer completes in useEffect or when the dismiss button is clicked before the timer completes.
   */
  const handleDismiss = () => {
    addAction('enterprise::privacy-policy-banner::dismissed', {
      message: `Privacy policy banner dismissed`,
      termsDocumentUuid: latestPrivacyPolicy?.uuid,
    });

    if (timer) {
      clearTimeout(timer);
      if (latestPrivacyPolicy) {
        markTermsAsSeen({ termsDocumentUuid: latestPrivacyPolicy.uuid });
      }
    }

    if (latestPrivacyPolicy) {
      markTermsAsAcknowledged(latestPrivacyPolicy.uuid);
    }
  };

  return (
    <div className="h-10 w-full bg-lightBlue-500 px-4 py-2 sm:py-0">
      <div className="flex h-full flex-row items-center justify-between md:justify-center">
        <p className="flex-1 text-center text-xs sm:text-left sm:text-sm md:text-center md:text-base">
          <Trans
            i18nKey="banner.privacyPolicyUpdate.description"
            components={{
              privacyLink: (
                <a
                  className="font-bold"
                  href={privacyLink}
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={handlePrivacyLinkClick}
                >
                  {/* NOTE: This text is a placeholder and will be swapped out with value in json file tag */}
                  privacyLink
                </a>
              ),
            }}
          />
        </p>
        <button
          aria-label={t('core.close')}
          className="z-20 ml-4 p-1 text-xl transition-all duration-200 hover:bg-black/10"
          type="button"
          onClick={handleDismiss}
        >
          <TimesIcon className="h-7 w-7 fill-text-primary" />
        </button>
      </div>
    </div>
  );
};

const PrivacyPolicyBanner = () => {
  return (
    <QueryBoundaries>
      <PrivacyPolicyBannerCore />
    </QueryBoundaries>
  );
};

export default PrivacyPolicyBanner;
